import { Layout } from 'react-grid-layout';
import { create } from 'zustand';

import { supabase } from '@/common/config/supabase';
import { withAuthUser } from '@/common/config/supabaseMiddleware';
import { useBoardStore } from '@/common/stores/useBoardStore';
import { useSubscriptionStore } from '@/common/stores/useSubscriptionStore';
import handleException from '@/common/utils/errors';

export interface Widget {
  id: string;
  widget_id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  metadata: Record<string, unknown>;
  discarded_at: string;
  created_at: string;
  updated_at: string;
}

interface WidgetStore {
  layouts: Layout[];
  widgets: Widget[];
  loading: boolean;
  fetchWidgets: () => Promise<void>;
  saveLayout: (layout: Layout[]) => Promise<void>;
  removeWidget: (widgetId: string) => Promise<void>;
  addWidget: (widgetId: string) => Promise<void>;
  getDiscardedWidgets: (widgetId: string) => Promise<Widget[]>;
  recoverWidget: (widget: Widget) => Promise<void>;
  updateWidget: (widgetId: string, updates: Partial<Widget>) => Promise<void>;
}

export const useWidgetStore = create<WidgetStore>((set, get) => ({
  layouts: [],
  widgets: [],
  loading: false,

  fetchWidgets: async () => {
    const currentBoard = useBoardStore.getState().currentBoard;
    if (!currentBoard) return;

    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('widgets')
        .select('*')
        .eq('board_id', currentBoard.id)
        .is('discarded_at', null);
      if (error) throw error;

      const layouts: Layout[] = data.map((widget) => ({
        i: widget.id,
        x: widget.x,
        y: widget.y,
        w: widget.w,
        h: widget.h,
      }));

      set({ widgets: data, layouts });
    } catch (error) {
      handleException(error);
    } finally {
      set({ loading: false });
    }
  },

  saveLayout: async (layout: Layout[]) => {
    const currentBoard = useBoardStore.getState().currentBoard;
    if (!currentBoard) return;

    try {
      await withAuthUser(async (userId) => {
        const existingWidgets = get().widgets;

        const { error } = await supabase.from('widgets').upsert(
          layout.map((item) => {
            const existingWidget = existingWidgets.find(w => w.id === item.i);
            return {
              id: existingWidget?.id,
              widget_id: existingWidget?.widget_id,
              board_id: currentBoard.id,
              x: item.x,
              y: item.y,
              w: item.w,
              h: item.h,
              user_id: userId,
              metadata: existingWidget?.metadata || {},
            };
          }),
          { onConflict: 'id' }
        );

        if (error) throw error;
      });

      set({ layouts: layout });
    } catch (error) {
      handleException(error);
    }
  },

  removeWidget: async (widgetId: string) => {
    const currentBoard = useBoardStore.getState().currentBoard;
    if (!currentBoard) return;

    try {
      const { error } = await supabase
        .from('widgets')
        .update({ discarded_at: new Date().toISOString() })
        .eq('board_id', currentBoard.id)
        .eq('id', widgetId);

      if (error) throw error;

      // Update local state
      const updatedWidgets = get().widgets.filter(w => w.id !== widgetId);
      const updatedLayouts = get().layouts.filter(l => l.i !== widgetId);

      set({
        widgets: updatedWidgets,
        layouts: updatedLayouts
      });
    } catch (error) {
      handleException(error);
    }
  },

  addWidget: async (widgetType: string) => {
    const currentBoard = useBoardStore.getState().currentBoard;
    if (!currentBoard) return;

    const { canAddWidget } = useSubscriptionStore.getState();
    if (!canAddWidget(widgetType, get().widgets.length)) {
      return;
    }

    try {
      await withAuthUser(async (userId) => {
        // Add new widget
        const { data: newWidgets, error: addError } = await supabase.from('widgets').insert({
          widget_id: widgetType,
          board_id: currentBoard.id,
          user_id: userId,
          x: 0,  // Default position
          y: get().widgets.length * 4, // Stack widgets vertically
          w: 6,  // Default width
          h: 4,  // Default height
          metadata: {},
        }).select();

        if (addError) throw addError;
        if (!newWidgets || newWidgets.length === 0) throw new Error('No widget data returned');

        const newWidget = newWidgets[0];
        const newLayout = {
          i: newWidget.id,
          x: newWidget.x,
          y: newWidget.y,
          w: newWidget.w,
          h: newWidget.h,
        };

        set({
          widgets: [...get().widgets, newWidget],
          layouts: [...get().layouts, newLayout]
        });
      });
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'code' in error && error.code === "23505") {
        return; // Ignore duplicate widget errors
      }
      handleException(error);
    }
  },

  getDiscardedWidgets: async (widgetId: string) => {
    const currentBoard = useBoardStore.getState().currentBoard;
    if (!currentBoard) return [];

    try {
      const { data, error } = await supabase
        .from('widgets')
        .select('*')
        .eq('board_id', currentBoard.id)
        .eq('widget_id', widgetId)
        .not('discarded_at', 'is', null)
        .order('discarded_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleException(error);
      return [];
    }
  },

  recoverWidget: async (widget: Widget) => {
    const currentBoard = useBoardStore.getState().currentBoard;
    if (!currentBoard) return;

    try {
      const { data, error } = await supabase
        .from('widgets')
        .update({ discarded_at: null })
        .eq('id', widget.id)
        .eq('board_id', currentBoard.id)
        .select()
        .single();

      if (error) throw error;
      if (!data) throw new Error('No widget data returned');

      set((state) => ({
        widgets: [...state.widgets, data],
        layouts: [...state.layouts, {
          i: data.id,
          x: data.x,
          y: data.y,
          w: data.w,
          h: data.h,
        }]
      }));
    } catch (error) {
      handleException(error);
      throw error;
    }
  },

  updateWidget: async (widgetId: string, updates: Partial<Widget>) => {
    const currentBoard = useBoardStore.getState().currentBoard;
    if (!currentBoard) return;

    try {
      const { data, error } = await supabase
        .from('widgets')
        .update(updates)
        .eq('id', widgetId)
        .eq('board_id', currentBoard.id)
        .select()
        .single();

      if (error) throw error;
      if (!data) throw new Error('No widget data returned');

      // Update local state
      set((state) => ({
        widgets: state.widgets.map(w =>
          w.id === widgetId ? { ...w, ...data } : w
        )
      }));
    } catch (error) {
      handleException(error);
      throw error;
    }
  },
}));
